import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CustomSelectField from 'components/forms/customSelectField/CustomSelectField';
import { VrboCancellationPolicy } from 'models/Channels';
import PropertyFormFieldLabel from 'pages/property/common/PropertyFormFieldLabel';
import TooltipContentWithLearnMore from 'pages/property/common/tooltip/TooltipContentWithLearnMore';
import { PropertySettingsMainTabFormValues } from '../../PropertySettingsMainTab.schema';

const optionsOrder = [
  VrboCancellationPolicy.RELAXED,
  VrboCancellationPolicy.MODERATE,
  VrboCancellationPolicy.FIRM,
  VrboCancellationPolicy.STRICT,
  VrboCancellationPolicy.NO_REFUND,
  VrboCancellationPolicy.CUSTOM,
];

const PropertyVrboSettingsCancellationPolicyField = () => {
  const { t } = useTranslation();
  const { setValue, getValues } =
    useFormContext<PropertySettingsMainTabFormValues>();

  const fieldName = 'vrboSettings.cancellationPolicy';
  const fieldLabel = (
    <PropertyFormFieldLabel name={fieldName}>
      {t('pageProperty.mainSettings.vrboSettings.cancellationPolicy.label')}
    </PropertyFormFieldLabel>
  );

  const options = optionsOrder.map((value) => ({
    label: t(
      `pageProperty.mainSettings.vrboSettings.cancellationPolicy.option_${value}`,
    ),
    value,
  }));

  const onPolicyChange = (value: string | null) => {
    if (
      value === VrboCancellationPolicy.CUSTOM &&
      !getValues('vrboSettings.customCancellationPolicies')?.length
    ) {
      setValue('vrboSettings.customCancellationPolicies', [
        { id: crypto.randomUUID(), refundPercentage: 0, refundThreshold: 0 },
      ]);
    }
  };

  const optionRenderer = ({
    children,
    data: { value },
  }: {
    children: React.ReactNode;
    data: { value: string };
  }) => (
    <div data-testid={`cancellationPolicyOption-${value}`}>
      <div data-testid="label">
        <b>{children}</b>
      </div>
      <div data-testid="description">
        {t(
          `pageProperty.mainSettings.vrboSettings.cancellationPolicy.optionDescription_${
            value as VrboCancellationPolicy
          }`,
        )}
      </div>
    </div>
  );

  return (
    <CustomSelectField
      label={fieldLabel}
      name={fieldName}
      onChangeHandler={onPolicyChange}
      options={options}
      optionRenderer={optionRenderer}
      colSmInput={7}
      colSmLabel={5}
      required
      rightTooltip={
        <TooltipContentWithLearnMore
          labelTooltipText={t(
            'pageProperty.mainSettings.vrboSettings.cancellationPolicyTooltip',
          )}
          learnMoreUrl="https://help.hostfully.com/en/articles/9405853-optimize-and-improve-your-listings-performance-on-vrbo#h_22c00b1bd9"
        />
      }
    />
  );
};

export default PropertyVrboSettingsCancellationPolicyField;
